import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { get as commsGet } from "../../../service/comms/commsApi";
import {
  getAnalyticsCoaches,
  classifyOversightError,
} from "../../../service/comms/oversightAnalyticsService";
import { presetRange, resolvedId } from "../../../utils/oversightFormat";
import { DateRangePreset } from "../../../common/comms/DateRangePreset";
import { EmptyState } from "../../../common/comms/EmptyState";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { useOversightAccess } from "../../../hooks/comms/OversightAccessProvider";
import { CoachSummaryTable } from "./CoachSummaryTable";
import { CoachSummaryCards } from "./CoachSummaryCards";
import { RoomsTab } from "../Oversight/RoomsTab";
import { ApiContext } from "../../../context/MyContext";
import { isExecutiveEmployee } from "../../../utils/accessControl";
import { isCoachManager } from "../Bookings/coachRoster";
import { useTeamLeaves } from "../../../features/leaves/workflow/useTeamLeaves";
import ManagerLeavesTab from "../../../features/leaves/ui/ManagerLeavesTab";

const PAGE_SIZE = 50;
const DEFAULT_PRESET_DAYS = 30;
/** Silent presence-refresh cadence while the Coaches tab is visible. */
const PRESENCE_POLL_MS = 60000;

/**
 * Coaches | Rooms | Leaves tabs, driven by the `?tab=` search param (absent =
 * coaches). Leaves is for the people who review leave: coach managers and
 * executives.
 */
const TABS = [
  { id: "coaches", label: "Coaches" },
  { id: "rooms", label: "Rooms" },
  { id: "leaves", label: "Leaves" },
];

/**
 * Read the payload-level qualifiers off a coaches-analytics response.
 * Absent flags are read optimistically (exact, not partial) so an older
 * backend that doesn't send them renders no misleading caveat.
 * @param {Object|null|undefined} payload
 */
const readMeta = (payload) => ({
  total: typeof payload?.total === "number" ? payload.total : null,
  hiddenUnmapped: payload?.hidden_unmapped_coaches ?? 0,
  partial: payload?.partial === true,
  percentilesExact: payload?.percentiles_exact !== false,
  readLatencyExact: payload?.read_latency_exact !== false,
});

/**
 * Whether another page exists. The roster-driven list returns zeroed rows for
 * quiet coaches, so a page shorter than PAGE_SIZE is not proof of the end —
 * trust `has_more` and only fall back to the length heuristic when the
 * backend doesn't send it.
 * @param {Object|null|undefined} payload
 * @param {Array} list - The page's rows.
 */
const morePages = (payload, list) =>
  typeof payload?.has_more === "boolean" ? payload.has_more : list.length >= PAGE_SIZE;

/**
 * Console shell + data layer for the `/coaches` oversight page.
 *
 * Tabbed (Coaches | Rooms, via `?tab=`) landing page for manager/executive
 * oversight. The Coaches tab pulls nightly-rollup analytics from
 * `GET /oversight/analytics/coaches`, supports a date-range preset, a
 * client-side name search, an offset-paginated "Load more", and a sort state
 * that's handed down to the presentational table/card components. The Rooms
 * tab renders the rooms browser (`RoomsTab`), deep-linkable via `?coach_id=`.
 */
export default function CoachAnalytics() {
  const navigate = useNavigate();
  const { reprobe } = useOversightAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const { employeeData, departmentsWithRoles } = useContext(ApiContext) || {};
  const canReviewLeaves =
    isCoachManager(employeeData, departmentsWithRoles) || isExecutiveEmployee(employeeData);
  const tabs = canReviewLeaves ? TABS : TABS.filter((t) => t.id !== "leaves");
  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.id === requestedTab) ? requestedTab : "coaches";
  // Owned here, not by the tab, so the tab badge counts pending requests
  // before the Leaves tab is ever opened.
  const teamLeaves = useTeamLeaves({ enabled: canReviewLeaves });

  const [presetDays, setPresetDays] = useState(DEFAULT_PRESET_DAYS);
  const [range, setRange] = useState(() => presetRange(DEFAULT_PRESET_DAYS));

  const [coaches, setCoaches] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  /**
   * Payload-level flags that qualify what the rows mean. `total`/`has_more`
   * drive pagination (the list is roster-driven, so a short page is NOT the
   * end); the rest are caveats the table would otherwise imply away.
   * @type {[{total: number|null, hiddenUnmapped: number, partial: boolean,
   *   percentilesExact: boolean, readLatencyExact: boolean}, Function]}
   */
  const [meta, setMeta] = useState({
    total: null,
    hiddenUnmapped: 0,
    partial: false,
    percentilesExact: true,
    readLatencyExact: true,
  });
  /** `reports` (own chain, the manager default) | `company` — see §13.16. */
  const [scope, setScope] = useState("reports");

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  /** @type {[('gateway'|'other'|'forbidden'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  /** @type {[('gateway'|'other'|null), Function]} */
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [search, setSearch] = useState("");
  /** @type {[{key: string|null, dir: ('asc'|'desc'|null)}, Function]} */
  const [sort, setSort] = useState({ key: null, dir: null });

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Kept in sync so the poll effect can read the current row count without
  // taking `coaches` as a dependency (which would reset the interval on
  // every poll tick).
  const coachesRef = useRef(coaches);
  useEffect(() => {
    coachesRef.current = coaches;
  }, [coaches]);

  // Initial/preset/retry fetch — always starts at offset 0.
  useEffect(() => {
    if (activeTab !== "coaches") {
      return undefined;
    }
    let active = true;
    setLoading(true);
    setErrorKind(null);
    setLoadMoreError(null);
    getAnalyticsCoaches(commsGet, { ...range, limit: PAGE_SIZE, offset: 0, scope })
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload?.coaches) ? payload.coaches : [];
        setCoaches(list);
        setOffset(0);
        setHasMore(morePages(payload, list));
        setMeta(readMeta(payload));
      })
      .catch((err) => {
        if (!active) return;
        const kind = classifyOversightError(err);
        setErrorKind(kind);
        setCoaches([]);
        setHasMore(false);
        setMeta(readMeta(null));
        if (kind === "forbidden") {
          reprobe();
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [range, reloadToken, activeTab, scope, reprobe]);

  // Live presence polling — silently refreshes `is_online_now` /
  // `online_seconds_today` for every row currently loaded, every
  // PRESENCE_POLL_MS, while the Coaches tab is mounted and the page is
  // visible. This is a MERGE, never a replace: it fetches (at most) as many
  // rows as are currently loaded and patches only the two live fields onto
  // matching existing rows by resolved id, leaving everything else —
  // including rows beyond the poll's own fetch and `offset`/`hasMore` —
  // untouched. A straight `setCoaches(list)` here would race a concurrent
  // "Load more": the poll's limit is captured at tick time, so it can
  // permanently truncate the list back down and silently drop a page.
  // Failures are swallowed except 'forbidden', which triggers a reprobe
  // like the other fetches.
  useEffect(() => {
    if (activeTab !== "coaches") {
      return undefined;
    }
    let missedTick = false;

    const silentRefetch = () => {
      if (document.visibilityState !== "visible") {
        missedTick = true;
        return;
      }
      const loaded = coachesRef.current.length;
      const pollLimit = loaded > PAGE_SIZE ? loaded : PAGE_SIZE;
      getAnalyticsCoaches(commsGet, { ...range, limit: pollLimit, offset: 0, scope })
        .then((payload) => {
          if (!mountedRef.current) return;
          const list = Array.isArray(payload?.coaches) ? payload.coaches : [];
          const byId = new Map(
            list.map((coach) => [resolvedId(coach) ?? coach.coach_user_id, coach])
          );
          setCoaches((cur) =>
            cur.map((row) => {
              const live = byId.get(resolvedId(row) ?? row.coach_user_id);
              return live
                ? {
                    ...row,
                    is_online_now: live.is_online_now,
                    online_seconds_today: live.online_seconds_today,
                  }
                : row;
            })
          );
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          if (classifyOversightError(err) === "forbidden") {
            reprobe();
          }
          // Any other failure is ignored silently — keep showing current data.
        });
    };

    const intervalId = setInterval(silentRefetch, PRESENCE_POLL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && missedTick) {
        missedTick = false;
        silentRefetch();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeTab, range, scope, reprobe]);

  const handlePresetChange = ({ days, date_from, date_to }) => {
    setPresetDays(days);
    setRange({ date_from, date_to });
    setOffset(0);
  };

  const handleRetry = () => setReloadToken((t) => t + 1);

  // Widening to company scope re-resolves the whole roster, so the current
  // page cursor is meaningless — the fetch effect restarts at offset 0.
  const handleShowCompanyWide = () => {
    setOffset(0);
    setScope("company");
  };

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    setLoadMoreError(null);
    getAnalyticsCoaches(commsGet, { ...range, limit: PAGE_SIZE, offset: nextOffset, scope })
      .then((payload) => {
        if (!mountedRef.current) return;
        const list = Array.isArray(payload?.coaches) ? payload.coaches : [];
        setCoaches((cur) => [...cur, ...list]);
        setOffset(nextOffset);
        setHasMore(morePages(payload, list));
        setMeta(readMeta(payload));
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        const kind = classifyOversightError(err);
        if (kind === "forbidden") {
          reprobe();
          return;
        }
        setLoadMoreError(kind);
      })
      .finally(() => {
        if (mountedRef.current) setLoadingMore(false);
      });
  };

  const handleTabChange = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === "coaches") {
      next.delete("tab");
    } else {
      next.set("tab", tabId);
    }
    setSearchParams(next);
  };

  // `resolvedId` first, matching how every list surface already identifies a
  // coach — row keys, names, and the presence-poll merge above all use it, and
  // its precedence puts `resolved_coach_user_id` ahead of `coach_user_id`.
  // Reading the raw field alone left a row that renders perfectly — real name,
  // real metrics, live presence dot — silently doing nothing when clicked.
  // `scope` rides along so the detail page resolves the same roster this list
  // did; without it, a coach reached via "Show company-wide" 403s on arrival.
  const handleOpenCoach = (coach) => {
    const id = resolvedId(coach);
    if (id == null) return;
    navigate(`/coaches/${id}${scope === "company" ? "?scope=company" : ""}`);
  };

  const handleViewRooms = (coach) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "rooms");
    const id = resolvedId(coach);
    if (id != null) {
      next.set("coach_id", id);
    }
    setSearchParams(next);
  };

  const visibleCoaches = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term
      ? coaches.filter((c) => (c.coach_name || "").toLowerCase().includes(term))
      : coaches;

    if (!sort.key) {
      return list;
    }

    const sorted = [...list];
    sorted.sort((a, b) => {
      const aVal = a?.[sort.key];
      const bVal = b?.[sort.key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "string" || typeof bVal === "string") {
        return sort.dir === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }
      return sort.dir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [coaches, search, sort]);

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-[calc(100vh-4.5rem)]">
      <header className="min-w-0">
        <h1 className="text-xl font-bold text-gray-900">
          Coaches <span className="text-gray-300 font-normal">·</span>{" "}
          <span className="text-gray-500 font-medium">
            {activeTab === "leaves" ? "leaves" : "performance"}
          </span>
        </h1>
      </header>
      {activeTab === "leaves" ? (
        <p className="text-xs text-gray-400 mt-1 mb-4">
          {isExecutiveEmployee(employeeData)
            ? "Leave requests from every coach"
            : "Leave requests from coaches assigned to you"}
        </p>
      ) : (
        <p className="text-xs text-gray-400 mt-1 mb-4">
          Aggregates from nightly rollup · presence is live
          {activeTab === "coaches" && meta.partial
            ? " · today included and still counting"
            : ""}
          {activeTab === "coaches" && scope === "company" ? " · company-wide" : ""}
        </p>
      )}

      <div className="flex gap-1 border-b border-gray-200 mb-4" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`min-h-[44px] px-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === tab.id
                ? "border-orange-primary text-orange-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.id === "leaves" && teamLeaves.pendingCount > 0 && (
              <span
                className="text-[11px] font-extrabold bg-[#FFF0E6] text-[#C2410C] px-[7px] py-px rounded-full"
                aria-label={`${teamLeaves.pendingCount} pending`}
              >
                {teamLeaves.pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "rooms" ? (
        <RoomsTab />
      ) : activeTab === "leaves" ? (
        <ManagerLeavesTab team={teamLeaves} />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-0 sm:max-w-xs">
              <FiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={15}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search coach"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-primary/30 focus:border-orange-primary"
              />
            </div>

            <DateRangePreset
              value={presetDays}
              onChange={handlePresetChange}
              className="w-full sm:w-auto"
            />
          </div>

          {/* Coaches with no node in the corporate reporting hierarchy can't
              be placed in a manager's report chain, so they're hidden rather
              than dropped silently. `?scope=company` is the documented way to
              see them; it's a no-op for executives, who never see this. */}
          {meta.hiddenUnmapped > 0 && scope !== "company" && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="m-0 flex-1 text-xs text-amber-900">
                {meta.hiddenUnmapped}{" "}
                {meta.hiddenUnmapped === 1 ? "coach isn't" : "coaches aren't"} mapped to the
                reporting hierarchy, so they're not in your team view. Ask an admin to map
                them, or view the whole company.
              </p>
              <button
                type="button"
                onClick={handleShowCompanyWide}
                className="min-h-[44px] self-start sm:self-auto px-3 text-xs font-semibold text-amber-900 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 whitespace-nowrap"
              >
                Show company-wide
              </button>
            </div>
          )}

          {errorKind === "forbidden" ? null : errorKind === "gateway" ? (
            <RetryBanner onRetry={handleRetry} />
          ) : errorKind === "other" ? (
            <RetryBanner
              message="Something went wrong loading coach analytics."
              onRetry={handleRetry}
            />
          ) : loading ? (
            <SkeletonRows variant="rows" count={6} />
          ) : visibleCoaches.length === 0 ? (
            coaches.length > 0 ? (
              <EmptyState title="No coaches match your search" />
            ) : (
              <EmptyState title="No coaches in your scope for this period" />
            )
          ) : (
            <>
              <div className="hidden sm:block">
                <CoachSummaryTable
                  coaches={visibleCoaches}
                  loading={loading}
                  sort={sort}
                  onSortChange={setSort}
                  onOpenCoach={handleOpenCoach}
                  onViewRooms={handleViewRooms}
                />
              </div>
              <div className="sm:hidden">
                <CoachSummaryCards
                  coaches={visibleCoaches}
                  loading={loading}
                  onOpenCoach={handleOpenCoach}
                  onViewRooms={handleViewRooms}
                />
              </div>

              <div className="mt-2 space-y-0.5">
                {meta.total != null && (
                  <p className="text-[11px] text-gray-400">
                    Showing {coaches.length} of {meta.total} coaches on your roster
                  </p>
                )}
                {/* Beyond 92 days the backend stops computing these exactly —
                    label them as approximations rather than implying precision. */}
                {(!meta.percentilesExact || !meta.readLatencyExact) && (
                  <p className="text-[11px] text-gray-400">
                    {!meta.percentilesExact && !meta.readLatencyExact
                      ? "Median, p90 and read latency are approximate over this range."
                      : !meta.percentilesExact
                        ? "Median and p90 are approximate over this range."
                        : "Read latency is approximate over this range."}
                  </p>
                )}
              </div>

              {loadMoreError && (
                <div className="mt-3">
                  <RetryBanner
                    message={
                      loadMoreError === "gateway"
                        ? "Couldn't load more coaches."
                        : "Something went wrong loading more coaches."
                    }
                    onRetry={handleLoadMore}
                  />
                </div>
              )}

              {hasMore && !loadMoreError && (
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="min-h-[44px] px-4 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-orange-50 disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
